import * as React from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Link from '@mui/material/Link'
import Button from '@mui/material/Button'
import { useTheme } from '@mui/material/styles'

import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'

import { cardSx } from './panelLayout'

/**
 * Building blocks of the "connect your tools" cards shared by /apps and
 * /api-access: a collapsed entry card, the setup panel it expands into, and
 * the copy buttons both use.
 */

// Copyable one-liner. It wraps rather than truncating — an ellipsized command
// is one the reader can't check before running it.
const snippetPillSx = theme => ({
    display: 'flex',
    alignItems: 'center',
    gap: 0.25,
    pl: 1,
    pr: 0.25,
    py: 0.25,
    borderRadius: '8px',
    border: `1px solid ${theme.palette.divider}`,
    bgcolor: theme.palette.action.hover,
})

/**
 * Collapsed entry point for one integration: a title, a button that opens the
 * full setup steps, one line of context, and the snippet a returning user just
 * wants to copy without reading the instructions again.
 */
export function ConnectCard({ title, description, snippet, onOpen }) {
    const theme = useTheme()
    return (
        <Box sx={{ ...cardSx(theme), p: { xs: 2.5, sm: 3 } }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 1.5,
                }}
            >
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {title}
                </Typography>
                <Button
                    size="small"
                    variant="outlined"
                    color="info"
                    onClick={onOpen}
                    endIcon={<ArrowForwardOutlinedIcon sx={{ fontSize: 14 }} />}
                    sx={{
                        flexShrink: 0,
                        borderRadius: '999px',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '12px',
                        lineHeight: 1.4,
                        py: 0.25,
                        px: 1.25,
                        '& .MuiButton-endIcon': { ml: 0.5 },
                    }}
                >
                    Setup steps
                </Button>
            </Box>
            <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.75 }}
            >
                {description}
            </Typography>
            <Box sx={{ ...snippetPillSx(theme), mt: 1.5 }}>
                <Box
                    component="code"
                    sx={{
                        fontSize: '12px',
                        color: 'text.secondary',
                        minWidth: 0,
                        // Wrap at the spaces first — `break-all` splits
                        // "--skill" and the next line reads as "kill …".
                        overflowWrap: 'anywhere',
                        lineHeight: 1.5,
                    }}
                >
                    {snippet}
                </Box>
                <InlineCopyButton text={snippet} />
            </Box>
        </Box>
    )
}

/**
 * An opened setup panel. Carries its own title and close control because the
 * collapsed card it replaces has left the rail by the time this renders.
 */
export function SetupPanel({ title, onHide, children }) {
    const theme = useTheme()
    return (
        <Box sx={{ ...cardSx(theme), p: { xs: 2.5, sm: 3 }, width: '100%' }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    gap: 1,
                    mb: 1.5,
                }}
            >
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {title}
                </Typography>
                <Link
                    component="button"
                    type="button"
                    underline="hover"
                    color="info.main"
                    onClick={onHide}
                    sx={{ flexShrink: 0, fontWeight: 600 }}
                >
                    Hide
                </Link>
            </Box>
            {children}
        </Box>
    )
}

export function CopyButton({ text }) {
    const [copied, setCopied] = React.useState(false)

    const handleCopy = () => {
        navigator.clipboard.writeText(text)
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
    }

    return (
        <Tooltip title={copied ? 'Copied!' : 'Copy'} placement="top">
            <IconButton
                size="small"
                onClick={handleCopy}
                sx={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    opacity: 0.6,
                    '&:hover': { opacity: 1 },
                    p: 0.5,
                }}
            >
                {copied ? (
                    <CheckIcon sx={{ fontSize: 14 }} />
                ) : (
                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                )}
            </IconButton>
        </Tooltip>
    )
}

// Compact, inline-flow copy button (the pill version isn't absolutely
// positioned like CopyButton).
export function InlineCopyButton({ text }) {
    const [copied, setCopied] = React.useState(false)

    const handleCopy = () => {
        navigator.clipboard.writeText(text)
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
    }

    return (
        <Tooltip title={copied ? 'Copied!' : 'Copy'} placement="top">
            <IconButton
                size="small"
                onClick={handleCopy}
                sx={{ p: 0.5, opacity: 0.7, '&:hover': { opacity: 1 } }}
            >
                {copied ? (
                    <CheckIcon sx={{ fontSize: 14 }} />
                ) : (
                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                )}
            </IconButton>
        </Tooltip>
    )
}

/**
 * Compact numbered stepper — the /mcp and /skills page stepper scaled down to
 * a side panel: a numbered marker per step joined by a thin rail.
 */
export function SetupSteps({ children }) {
    return <Box sx={{ mt: 0.5 }}>{children}</Box>
}

export function SetupStep({ n, title, children }) {
    const theme = useTheme()
    return (
        <Box
            sx={{
                display: 'flex',
                gap: 1.25,
                position: 'relative',
                pb: 2,
                '&:last-of-type': { pb: 0 },
                '&:not(:last-of-type)::before': {
                    content: '""',
                    position: 'absolute',
                    left: 10,
                    top: 26,
                    bottom: 4,
                    width: '1px',
                    bgcolor: theme.palette.divider,
                },
            }}
        >
            <Box
                sx={{
                    flex: '0 0 22px',
                    height: 22,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #4054b2, #7b6cd9)',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '11px',
                    fontWeight: 700,
                }}
            >
                {n}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, lineHeight: '22px', mb: 0.5 }}
                >
                    {title}
                </Typography>
                {children}
            </Box>
        </Box>
    )
}

/** Segmented pill switcher for picking a client / agent inside a step. */
export function ClientSwitcher({ options, value, onChange }) {
    const theme = useTheme()
    return (
        <Box
            role="tablist"
            sx={{
                display: 'inline-flex',
                flexWrap: 'wrap',
                gap: '2px',
                p: '3px',
                mb: 0.75,
                borderRadius: '14px',
                bgcolor: theme.palette.action.hover,
            }}
        >
            {Object.entries(options).map(([key, label]) => {
                const active = key === value
                return (
                    <Box
                        key={key}
                        component="button"
                        type="button"
                        role="tab"
                        aria-selected={active}
                        onClick={() => onChange(key)}
                        sx={{
                            border: 0,
                            cursor: 'pointer',
                            borderRadius: '999px',
                            px: 1.25,
                            py: '3px',
                            fontSize: '11.5px',
                            fontWeight: 600,
                            fontFamily: 'inherit',
                            color: active ? '#fff' : 'info.main',
                            bgcolor: active ? 'info.main' : 'transparent',
                            transition: 'background-color 0.15s, color 0.15s',
                        }}
                    >
                        {label}
                    </Box>
                )
            })}
        </Box>
    )
}

/** Copyable code block; multi-line text keeps its line breaks. */
export function CodeSnippet({ text, wrap = false }) {
    const theme = useTheme()
    const multiline = text.includes('\n')
    return (
        <Box sx={{ position: 'relative', mb: 0.5 }}>
            <Box
                component={multiline ? 'pre' : 'code'}
                sx={{
                    display: 'block',
                    fontSize: multiline && !wrap ? '9.5px' : '11px',
                    bgcolor: theme.palette.action.hover,
                    borderRadius: '6px',
                    px: 1,
                    py: 0.5,
                    pr: 4,
                    m: 0,
                    overflow: 'auto',
                    whiteSpace: multiline && !wrap ? 'pre' : 'pre-wrap',
                    overflowWrap: 'anywhere',
                    lineHeight: 1.45,
                }}
            >
                {text}
            </Box>
            <CopyButton text={text} />
        </Box>
    )
}

/** Explanatory caption under a snippet, set off by an accent rule. */
export function StepNote({ children }) {
    const theme = useTheme()
    return (
        <Typography
            variant="caption"
            component="div"
            sx={{
                mt: 0.5,
                px: 1.25,
                py: 0.75,
                borderLeft: '3px solid',
                borderColor: 'info.main',
                bgcolor: theme.palette.action.hover,
                borderRadius: '0 6px 6px 0',
                lineHeight: 1.5,
            }}
        >
            {children}
        </Typography>
    )
}

/** Plain step text. */
export function StepText({ children, sx }) {
    return (
        <Typography
            variant="caption"
            component="div"
            color="text.secondary"
            sx={{ lineHeight: 1.5, mb: 0.5, ...sx }}
        >
            {children}
        </Typography>
    )
}
